/** Platform admin: tenant-detail screen (P9). Owned by the tenant-detail screen; fil mirrors every key. */
export const tenantDetail = {
  notAvailable: "Not available yet",
  soon: "Soon",
  notFound: "Tenant not found",
  notFoundBody: "It may have been deleted, or the link is wrong.",
  backToTenants: "Back to tenants",
  header: {
    since: "since {{date}}",
    owner: "Owner: {{owner}}",
    copyId: "Copy tenant ID {{id}}",
    copyFailed: "Couldn't copy — select the ID and copy it manually",
    addCredits: "Add credits",
    applyDiscount: "Apply discount",
    emailReport: "Email usage report",
    suspendedReason: "Suspended: {{reason}}",
    pinned: "Pinned notes",
  },
  tabs: {
    label: "Tenant sections",
    overview: "Overview",
    usage: "Usage",
    service: "Service Desk",
    billing: "Billing",
    members: "Members",
    agents: "Agents & Knowledge",
    limits: "Limits & Overrides",
    audit: "Audit",
    notes: "Notes",
    failures_one: "{{count}} ingestion failure",
    failures_other: "{{count}} ingestion failures",
  },
  service: {
    banner: "Ticket content is not visible to platform staff. Use audited impersonation if the tenant asks for help.",
    usageLabel: "Service Desk usage, {{month}}",
    off: { title: "Service Desk is not enabled for {{name}}", body: "The workspace has no Service Desk switched on, so there is no usage to show.", planBody: "The plan does not include the help desk, so there is no usage to show." },
    tickets: { title: "Tickets", info: "Tickets created this month. Fair use never blocks clients: owners are e-mailed at 80% and 100%.", of: "{{value}} fair use", unlimited: "no fair-use limit", percent: "{{value}}% of fair use", chart: "Tickets created per day, last {{count}} days", table: "Show as table", day: "Day", count: "Tickets" },
    orders: { title: "Orders", info: "Orders placed through chat, the web app or staff this month. Counts only; no amounts are shown to platform staff.", note: "Placed this month. No amounts shown to platform staff.", none: "Not in this plan" },
    percentOfLimit: "{{value}}% of the limit",
    catalogue: { title: "Catalogue items", info: "Active products cached from the tenant's purchasing system, against the plan limit.", of: "of {{value}}" },
    staffAi: { title: "Staff AI credits", info: "Credits charged for staff AI tasks this month. Client chat follows the normal message quota.", note: "Used this month for summaries, suggested replies and triage." },
    assets: { title: "Equipment", info: "Equipment in service (not decommissioned, returned or archived). A count only: no names or serial numbers.", of: "of {{value}}" },
    fieldSeats: { title: "Field seats", info: "Members whose only role is Field engineer, against the field seats the plan and add-ons give.", of: "of {{value}}" },
    storage: { title: "Attachment storage", info: "Photos, documents and signatures attached to tickets, separate from knowledge storage.", mb: "{{value}} MB", gb: "{{value}} GB", of: "of {{value}}", unlimited: "no limit", percent: "{{value}}% used" },
    connectors: { title: "Connectors", info: "Connectors in use against the plan limit, with the health of the last {{window}} calls.", none: "None set up", of: "of {{limit}}", calls: "{{ok}} ok, {{failed}} failed in the last {{total}} calls", noCalls: "No calls yet", health: { none: "No connector", healthy: "Healthy", degraded: "Degraded", unhealthy: "Unhealthy" }, notice: "Counts and status only. Call details and order content stay with the tenant." },
    fairUse: { title: "Fair use", sub: "{{used}} tickets of {{limit}} this month. Creating tickets is never blocked for clients.", subUnlimited: "{{used}} tickets this month. This plan has no fair-use limit.", at80: "Owners e-mailed at 80%", at100: "Owners e-mailed at 100%", at120: "CRUD.IT sales notified after 2 months over 120%", tickets: "{{value}} tickets", state: { below: "Below 80%", above80: "Above 80%", at100: "At 100%", above120: "Above 120%" } },
    support: { title: "Support access", body: "If the tenant asks for help with a ticket, an order or a connector, start an audited impersonation session. It is read-only: every page you open is written to the tenant's audit log with your reason, including ticket content.", button: "Request audited impersonation", readOnly: "Your role cannot start an impersonation session." },
    infoLabel: "What does {{title}} mean?",
    error: "We could not read the usage counters. Try again in a moment.",
  },
  overview: {
    kpis: "Tenant KPIs",
    mrr: "MRR",
    plan: "{{plan}} plan",
    messages: "Messages MTD",
    ofLimit: "of {{limit}} included",
    members: "Members",
    pendingInvites_one: "{{count}} pending invite",
    pendingInvites_other: "{{count}} pending invites",
    aiCost: "AI cost this cycle",
    margin: "margin {{pct}}",
    usageVsPlan: "Usage vs {{plan}} plan",
    overridden_one: "{{count}} override",
    overridden_other: "{{count}} overrides",
    messagesLabel: "Messages",
    messagesHint: "This month, including overrides",
    agentsLabel: "Agents",
    atLimit: "At limit · upsell an extra agent or a plan upgrade",
    storageLabel: "Storage",
    sources_one: "{{count}} source",
    sources_other: "{{count}} sources",
    seatsLabel: "Seats",
    knowledge: "Knowledge",
    liveAgents: "Live agents",
    sourcesLabel: "Sources",
    chunks: "Chunks",
    failures: "Ingestion failures",
    none: "None",
    agents: "Agents",
    msgsCycle: "Msgs this cycle",
    noAgentUsage: "No agent traffic in this period yet.",
    activity: "Recent activity",
    fullAudit: "Full audit",
    noActivity: "No activity yet",
    noActivityBody: "Events from this workspace appear here as they happen.",
  },
  usage: {
    range: "Usage range",
    ranges: { "30d": "Last 30 days", "90d": "90 days", cycle: "This cycle" },
    export: "Export usage",
    stats: "Usage totals",
    messages: "Messages",
    days_one: "{{count}} day",
    days_other: "{{count}} days",
    tokensIn: "Tokens in",
    tokensOut: "Tokens out",
    avgPerMsg: "avg {{value}} / msg",
    storage: "Storage",
    storageFoot: "Files and vectors",
    rerank: "Rerank calls",
    embeddingTokens: "{{value}} embedding tokens",
    perDay: "Messages per day",
    noMessages: "No messages in this period",
    noMessagesBody: "Traffic shows up here once the tenant's agents start answering.",
    margin: "AI cost vs revenue",
    revenue: "Revenue",
    aiCost: "LLM + embeddings + rerank",
    grossMargin: "Gross margin",
    fxNote: "Converted at {{fx}}/USD. Infra share (hosting, storage) is not included.",
    byAgent: "By agent",
    agent: "Agent",
    cost: "Cost",
    share: "Share of messages",
  },
  billing: {
    refund: {
      actions: "Payment actions",
      action: "Refund",
      title: "Refund payment",
      body: "Up to {{amount}} can be refunded through {{gateway}}. This can't be undone.",
      amount: "Amount ({{currency}})",
      amountHint: "Leave empty to refund everything left.",
      amountInvalid: "Enter an amount up to what is left to refund.",
      reason: "Reason (kept in the audit log)",
      confirm: "Refund",
      done: "Refunded · {{amount}} in total",
      refunded: "{{amount}} refunded",
    },
    subscription: "Subscription",
    plan: "Plan",
    price: "Price",
    pricePer: { monthly: "{{price}} / month", annual: "{{price}} / year" },
    nextInvoice: "Next invoice",
    email: "Billing email",
    status: "Status",
    subStatuses: { active: "Active", trialing: "Trialing", past_due: "Past due", canceled: "Cancelled", cancelled: "Cancelled", incomplete: "Incomplete" },
    cycle: "Billing cycle",
    cycles: { monthly: "Monthly", annual: "Annual" },
    discount: "Discount",
    discountActive: "Active",
    scheduled: "Scheduled change",
    scheduledYes: "Pending at renewal",
    noSubscription: "No subscription",
    noSubscriptionBody: "This workspace has no active subscription. Pick a plan to start billing.",
    applyDiscount: "Apply discount / credit",
    switchAnnual: "Switch to annual",
    invoices: "Invoices",
    orNote: "BIR official receipts issued on payment",
    invoice: "Invoice",
    period: "Period",
    amount: "Amount",
    paidVia: "Paid via",
    orNumber: "OR No.",
    invoiceStatuses: { paid: "Paid", open: "Open", draft: "Draft", void: "Void", uncollectible: "Uncollectible", failed: "Failed" },
    noInvoices: "No invoices yet.",
    payments: "Payment attempts",
    date: "Date",
    method: "Method",
    outcome: "Outcome",
    paymentStatuses: { succeeded: "Succeeded", paid: "Paid", pending: "Pending", failed: "Failed", refunded: "Refunded" },
    noPayments: "No payment attempts yet.",
    failedNotice_one: "{{count}} failed payment",
    failedNotice_other: "{{count}} failed payments",
    failedNoticeBody: "Check the payment attempts below. The gateway retries automatically.",
  },
  members: {
    title: "Team members",
    seats: "{{used}} of {{limit}} seats",
    seatsUnlimited: "{{used}} seats · unlimited",
    filter: "Filter members…",
    member: "Member",
    role: "Role",
    twoFactor: "2FA",
    lastActive: "Last active",
    roles: { owner: "Owner", admin: "Admin", editor: "Editor", viewer: "Viewer", agent: "Agent (Inbox)" },
    statuses: { invited: "Invited", disabled: "Disabled", suspended: "Suspended" },
    enrolled: "Enrolled",
    notEnrolled: "Not enrolled",
    reset: "Reset 2FA",
    resetFor: "Reset 2FA for {{member}}",
    remind: "Remind",
    noMatch: "No members match",
    noMatchBody: "Try a different name, email or role.",
    clear: "Clear filter",
    empty: "No members yet",
    emptyBody: "Invite the workspace owner from the tenant console.",
  },
  reset2fa: {
    title: "Reset 2FA for {{member}}?",
    description: "Their current authenticator is removed and they must enroll again at next sign-in. Only do this after verifying identity.",
    method: "Verification method",
    methods: { callback: "Callback to the registered landline", video_call: "Video call with ID", owner_request: "Request from tenant Owner" },
    submit: "Reset 2FA",
    done: "2FA reset for {{member}} · logged to audit",
  },
  knowledge: {
    agents: "Agents",
    agentStatuses: { live: "Live", draft: "Draft", paused: "Paused", archived: "Archived" },
    publicKey: "Public key",
    failures: "Ingestion failures",
    retryAll: "Retry all",
    retry: "Retry",
    retryFor: "Retry {{source}}",
    retried_one: "{{count}} source queued for re-ingestion",
    retried_other: "{{count}} sources queued for re-ingestion",
    noFailures: "No ingestion failures",
    noFailuresBody: "Every source parsed and embedded cleanly.",
    source: "Source",
    stage: "Stage",
    stages: { upload: "Upload", parse: "Parsing", ocr: "Parsing (OCR)", chunk: "Chunking", embed: "Embedding", index: "Indexing" },
    error: "Error",
    attempts: "Attempts",
    when: "When",
    sources: "Knowledge sources",
    summary_one: "{{count}} source · {{chunks}} chunks · {{mb}} MB",
    summary_other: "{{count}} sources · {{chunks}} chunks · {{mb}} MB",
    type: "Type",
    types: { file: "File", website: "Website", text: "Text", qa: "Q&A" },
    pages_one: "{{count}} page",
    pages_other: "{{count}} pages",
    chunks: "Chunks",
    status: "Status",
    statuses: { ready: "Ready", processing: "Processing", queued: "Queued", pending: "Pending", failed: "Failed", error: "Error" },
    synced: "Last synced",
    noSources: "No knowledge sources",
    noSourcesBody: "The tenant hasn't uploaded files or connected a website yet.",
  },
  graph: {
    title: "Knowledge graph",
    stage: "Experiment",
    info: "Entities and relations extracted from the active knowledge chunks, used as an extra retrieval branch. Platform staff see counts and run details only, never names or chunk text.",
    status: { active: "Active", off: "Off" },
    blockedBy: {
      platform_flag: "The platform flag graphrag_experiment is off for this workspace.",
      plan: "The workspace's plan does not include the knowledge graph.",
      tenant_override: "The graph is switched off for this workspace.",
      agent: "No agent is on Assist, so nothing is extracted.",
    },
    layers: {
      label: "Control layers",
      on: "On",
      off: "Off",
      flag: { name: "Platform flag", detail: "graphrag_experiment, global or rollout list" },
      plan: { name: "Plan feature", included: "Included", notIncluded: "Not included", detail: "graph_retrieval on the {{plan}} plan" },
      switch: { name: "Workspace switch", detail: "Overrides the plan for this workspace (Limits & Overrides)", inherit: "Follows plan", on: "Switched on", off: "Switched off" },
      agents: { name: "Agents", value: "{{assist}} of {{total}} on Assist", detail: "Agents whose knowledge graph setting is Assist", none: "This workspace has no agents yet" },
    },
    counts: { entities: "Entities", relations: "Relations" },
    empty: {
      title: "No graph yet",
      activeBody: "Every layer allows it. The graph fills in as sources are indexed; use Rebuild graph to extract from existing sources.",
      offBody: "A layer is off, so nothing is extracted. Switch the layers on to build a graph.",
      openLimits: "Open Limits & Overrides",
      openFlags: "Open Feature flags",
    },
    lastRun: {
      title: "Last extraction run",
      none: "No extraction run yet.",
      statuses: { queued: "Queued", running: "Running", succeeded: "Succeeded", failed: "Failed", deferred: "Deferred (over budget)", deferredSize: "Deferred (workspace too large)" },
      reasons: { graph_size_limit: "More active chunks than the graph limit (25,000 unless overridden): extraction and rebuild wait until sources are removed or the limit is raised. The existing graph keeps answering." },
      when: "When",
      chunks: "Chunks processed",
      added: "Added",
      addedValue: "{{entities}} entities · {{relations}} relations",
      model: "Model",
      cost: "Cost",
    },
    budget: {
      title: "Chunks this month",
      value: "{{used}} / {{limit}}",
      percent: "{{value}}% of the {{month}} allowance",
      overridden: "limit set for this workspace",
      none: "No monthly allowance on this plan. Set graph_monthly_chunks in Limits & Overrides to allow extraction.",
    },
    rebuild: {
      action: "Rebuild graph",
      title: "Rebuild the knowledge graph?",
      body: "Extracts the entities and relations of {{tenant}} again. It uses model tokens and counts against the monthly budget. One rebuild per workspace every 5 minutes.",
      confirm: "Rebuild",
      queued: "Rebuild queued for {{tenant}}",
    },
    errors: {
      disabled: "A control layer is off for this workspace, so the graph cannot be rebuilt.",
      notReady: "The graph extraction worker is not available on this deployment yet.",
    },
  },
  limits: {
    infoBefore: "Overrides apply to this tenant only and take priority over the ",
    planLink: "{{plan}} plan",
    infoAfter: " defaults. Leave a field blank to inherit the plan value. Every change is written to the audit log.",
    title: "Limits",
    fields: {
      message_credits: "Monthly message limit",
      max_file_mb: "Max file size (MB)",
      rate_limit_rpm: "Rate limit (requests / minute / widget)",
      storage_mb: "Storage (MB)",
      agents: "Agents",
      seats: "Seats",
      graph_monthly_chunks: "Knowledge graph budget (chunks / month)",
    },
    planDefault: "Plan default {{value}}",
    placeholder: "{{value}} (plan)",
    invalid: "Enter a whole number, or leave blank to inherit.",
    expires: "Overrides expire",
    expiresHint: "Optional. Limits fall back to the plan after this date.",
    allowedModels: "Allowed models",
    premium: "{{provider}} · Pro+",
    planModels: "Plan default models",
    modelsInherit: "Inherits the plan's models until you change the selection.",
    modelsRestricted: "Restricted to the selected models for this tenant.",
    save: "Save overrides",
    saved: "Overrides saved for {{tenant}}",
    resetDone: "Overrides reset to plan defaults",
  },
  flags: {
    title: "Feature flags",
    note: "Each switch saves immediately and is logged.",
    on: "{{flag}} enabled",
    off: "{{flag}} disabled",
    voice: { name: "Voice (beta)", description: "Speech in/out on web app" },
    mobile_white_label: { name: "Mobile white-label", description: "Own app name & icon in stores" },
    actions: { name: "Actions", description: "Booking, lead forms, webhooks" },
    custom_domains: { name: "Custom domains", description: "Serve the web app on the tenant's domain" },
    byok: { name: "BYO API key", description: "Tenant's own OpenAI/Anthropic key" },
    remove_branding: { name: "Remove “Powered by Sagot”", description: "Normally a paid add-on" },
    graph_retrieval: { name: "Knowledge graph (Experiment)", description: "Entity graph as an extra retrieval branch" },
  },
  audit: {
    search: "Search events…",
    time: "Time",
    actor: "Actor",
    event: "Event",
    target: "Target",
    ip: "IP",
    staff: "CRUD.IT · {{name}}",
    via: "via {{name}}",
    actorTypes: { staff: "CRUD.IT staff", tenant: "Member", system: "System", end_user: "End user", api_key: "API key" },
    loadMore: "Load more",
    empty: "No audit events yet",
    emptyBody: "Changes in this workspace are recorded here.",
    noMatch: "No events match",
    noMatchBody: "Try another event name, actor or target.",
    clear: "Clear search",
  },
  notes: {
    title: "Internal notes",
    visibility: "Visible to CRUD.IT staff only",
    unknownAuthor: "Former staff",
    categories: { account_management: "Account management", sales: "Sales", support: "Support", billing: "Billing", risk: "Risk" },
    pinned: "Pinned",
    pin: "Pin",
    unpin: "Unpin",
    pinnedDone: "Note pinned to the tenant header",
    unpinned: "Note unpinned",
    empty: "No notes yet",
    emptyBody: "Add context for the team — calls, promises, risks — with the form.",
    addTitle: "Add note",
    category: "Category",
    note: "Note",
    placeholder: "e.g. Called the owner re: renewal, wants annual billing from January…",
    required: "Write the note first.",
    pinToHeader: "Pin to tenant header",
    add: "Add note",
    added: "Note added",
  },
  trial: {
    action: "Extend trial",
    title: "Extend trial",
    descriptionTrial: "Adds free days to the trial and pushes the first invoice date.",
    descriptionPaid: "This tenant is on a paid plan. Extending gives free days on top of the current period and pushes the next invoice date.",
    days: "Extra days",
    reason: "Reason",
    reasonPlaceholder: "e.g. Delayed onboarding due to typhoon suspensions",
    reasonRequired: "Give a short reason (shown in the audit log).",
    submit: "Extend",
    done: "Trial extended by {{days}} days · now ends {{date}}",
  },
} as const;
